import { useSyncExternalStore } from "react";
import { boundedPromise, isDeadlineExceededError } from "@-/dev.improve.haniverse.lib.deadline";

export interface SyncFailure {
  /**
   * When this streak of same-kind failures began: a new failure of the same kind (even with a
   * different message) keeps the original time, so "failing since" survives every retry.
   */
  at: number;
  kind: "pull" | "push";
  message: string;
}

export interface SyncStatus {
  failure: SyncFailure | null;
  // Monotonic counter bumped once each time the actor DB's initial remote pull DRAINS to
  // completion (see routes/nav/_lib/turso/use-actor-turso-remote-sync.ts). 0 = no initial
  // pull has finished on this connection yet. Unlike a table-change-version (which rotates
  // on every individual write, including each pulled row), this fires exactly ONCE per
  // completed drain, so it's a clean "the synced deck has fully landed locally" edge —
  // the deterministic signal E2E (Maestro) blocks on instead of polling the UI for a
  // specific synced row. Surfaced to the accessibility tree by the dev BundleStamp footer.
  initialPullGeneration: number;
  /**
   * Where the actor remote-sync lifecycle currently is (e.g. `no-account`, `awaiting-handle`,
   * `pulling`). Purely diagnostic: release builds log only to Sentry, so the E2E sentinel
   * publishes this to the accessibility tree to make a never-draining sync explainable from a
   * Maestro hierarchy dump.
   */
  phase: string;
  /** Rows currently present in the local actor replica; not a transfer total or percentage. */
  syncedEntryCount: number | null;
}

let status: SyncStatus = {
  failure: null,
  initialPullGeneration: 0,
  phase: "idle",
  syncedEntryCount: null,
};

// Monotonic count of COMPLETED initial pull drains. Unlike `initialPullGeneration`
// above — which `markInitialPullStarted` RESETS to 0 whenever a fresh sync
// connection begins pulling (that reset is what re-arms the E2E accessibility
// sentinel) — this counter only ever increments, so "did a pull complete after
// point X?" comparisons stay valid across connection swaps. A generation-based
// comparison breaks on the second sign-in of a session: the reset drops the
// generation below the captured baseline, the completion bump only restores it to
// the same value, and the waiter times out instead of observing the drain.
let initialPullCompletionCount = 0;

const listeners = new Set<() => void>();
const failuresByDirection = new Map<SyncFailure["kind"], SyncFailure>();

function emit(): void {
  for (const listener of listeners) {
    listener();
  }
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

function getSnapshot(): SyncStatus {
  return status;
}

export function useSyncStatus(): SyncStatus {
  return useSyncExternalStore(subscribe, getSnapshot, getSnapshot);
}

export function getSyncStatus(): SyncStatus {
  return status;
}

export function setSyncedEntryCount(syncedEntryCount: number | null): void {
  if (syncedEntryCount === status.syncedEntryCount) {
    return;
  }
  status = { ...status, syncedEntryCount };
  emit();
}

export function setSyncPhase(phase: string): void {
  if (phase === status.phase) {
    return;
  }
  status = { ...status, phase };
  emit();
}

export function setSyncFailure(failure: SyncFailure | null): void {
  if (failure === null) {
    clearSyncFailure();
    return;
  }
  const previous = failuresByDirection.get(failure.kind);
  if (previous !== undefined) {
    failure = { ...failure, at: previous.at };
  }
  failuresByDirection.set(failure.kind, failure);
  if (
    failure === status.failure ||
    (failure != null &&
      status.failure != null &&
      failure.kind === status.failure.kind &&
      failure.message === status.failure.message)
  ) {
    return;
  }
  status = {
    ...status,
    failure:
      failure != null && status.failure?.kind === failure.kind
        ? { ...failure, at: status.failure.at }
        : failure,
  };
  emit();
}

export function clearSyncFailure(kind?: SyncFailure["kind"]): void {
  if (kind === undefined) {
    failuresByDirection.clear();
  } else {
    failuresByDirection.delete(kind);
  }
  if (status.failure == null || (kind !== undefined && status.failure.kind !== kind)) {
    return;
  }
  const failure = failuresByDirection.values().next().value ?? null;
  status = { ...status, failure };
  emit();
}

/**
 * Reset {@link SyncStatus.initialPullGeneration} when a new actor sync connection
 * starts its initial pull. The accessibility label returns to `actor-syncing`, so
 * E2E flows cannot satisfy their wait with a stale `actor-synced gen N` from a
 * previous account/connection.
 */
export function markInitialPullStarted(): void {
  if (status.initialPullGeneration === 0 && status.syncedEntryCount === null) {
    return;
  }
  status = { ...status, initialPullGeneration: 0, syncedEntryCount: null };
  emit();
}

/**
 * Bump {@link SyncStatus.initialPullGeneration} — call once when the actor DB's
 * initial remote pull has finished draining every batch. The increment is the
 * deterministic "synced deck is fully local now" edge that the dev BundleStamp
 * footer publishes to the accessibility tree (`actor-synced gen N`), so an E2E
 * flow can wait on the count advancing rather than racing the sync by polling
 * for a specific synced row.
 */
export function markInitialPullComplete(): void {
  initialPullCompletionCount += 1;
  status = { ...status, initialPullGeneration: status.initialPullGeneration + 1 };
  emit();
}

/**
 * The current value of the monotonic completed-initial-pull counter. Capture it
 * before kicking off (or observing) a sync bootstrap, then pass it to
 * {@link waitForInitialPullCompletion} to wait for the NEXT drain to finish.
 */
export function getInitialPullCompletionCount(): number {
  return initialPullCompletionCount;
}

/**
 * Resolve once the completed-initial-pull counter advances past `fromCount` (i.e.
 * a sync connection has finished draining a first remote pull since the caller's
 * snapshot), or once a pull `failure` is recorded — whichever comes first. This is
 * the settle signal the sign-in deck bootstrap keys off: it releases the actor
 * read gate, which lets every held actor query resolve against the freshly-pulled
 * deck. Counter-based (not generation-based) so it survives
 * `markInitialPullStarted`'s generation reset on connection swaps — see the
 * counter's comment above.
 *
 * Bounded by `timeoutMs` so a wedged sync can't hold reads forever; on timeout it
 * resolves anyway (queries then show whatever synced, and background sync keeps
 * draining). Returns how it settled so the caller can log.
 */
export function waitForInitialPullCompletion(
  fromCount: number,
  timeoutMs = 20_000,
): Promise<"pulled" | "failed" | "timeout"> {
  if (initialPullCompletionCount > fromCount) {
    return Promise.resolve("pulled");
  }
  return boundedPromise<"pulled" | "failed">(
    { deadline: timeoutMs, operation: "initial actor pull completion" },
    (resolve) =>
      subscribe(() => {
        if (initialPullCompletionCount > fromCount) {
          resolve("pulled");
        } else if (status.failure?.kind === "pull") {
          resolve("failed");
        }
      }),
  ).catch((error: unknown) => {
    if (isDeadlineExceededError(error)) {
      return "timeout" as const;
    }
    throw error;
  });
}
